import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import ToolLinks from '@/components/page-specific/tools/tool-links'
import { useAuth, useUser } from '@clerk/nextjs'
import { ArrowRight, Check } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

export default function LiteratureReviewGenerator() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  return (
    <>
      <NextSeo
        title="v0 report - The Most Reliable AI Literature Review Generator 
        "
        description="Choose v0 Report, your go-to AI Literature Review Generator, for efficient literature paper creation. Perfect for students and researchers for academic support."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen flex-col ${inter.className}`}>
          <div className=" mt-36">
            <div className="mx-auto text-center">
              <h1 className="text-6xl text-zinc-700  grid gap-2">
                <span className="font-medium">v0 Report - The Most Reliable</span>
                <span className="font-thin italic font-serif leading-none text-yellow-600">
                  AI Literature Review Generator
                </span>
              </h1>
            </div>
            <div className="ml-auto flex px-4 md:px-0 md:w-3/4 mx-auto mb-24">
              <div className="w-full mx-auto">
                <div className="grid gap-6">
                  <h2 className="text-4xl text-zinc-700 text-left mt-48">
                    <span className="font-medium">Let v0 Report Enhance Your </span>
                    <br />
                    <span className="font-thin italic font-serif leading-none text-sky-600">
                      Literature Review with Just Five Prompts
                    </span>
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Cut down on the time and energy spent on literature reviews with our
                      AI-powered tool!
                    </span>
                    <span>
                      To make your research process smoother, create organized literature summaries
                      and high-quality literature reviews that enhance your academic writing
                      projects.
                    </span>
                    <span>All made possible by v0 Report.</span>
                  </p>
                  <Link
                    className="w-fit h-10 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                    href={'/auth/signup'}
                  >
                    {' '}
                    Get started <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/report.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    How the v0 Report Simplifies Literature Review Process
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">AI Literature Review Generator</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Effortlessly generate research papers and structure your literature
                            review with our AI-powered literature research assistant. Easily locate
                            sources for any sentence or text using our integrated research feature
                            for comprehensive literature reviews.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Versatile Literature Review Styles
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Select from various literature review formats, including descriptive
                            styles that simplify the writing process across different academic
                            conventions.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Professional and Top-Quality Literature Reviews
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Produce high-quality literature reviews suitable for college-level
                            assignments with our advanced writing quality mode.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Structured and Coherent Text</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Create a well-organized narrative with a logical flow and comprehensive
                            literature introductions and conclusions.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Automated Referencing</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Include references seamlessly in your literature review from Google,
                            Google Scholar, and other trusted sources.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Beat Writer’s Block</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Overcome writer’s block with our AI literature review generator,
                            facilitating the initiation and progression of your literature review.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/on-laptop.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Why Choose the v0 Report AI Literature Review Generator?
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Writing a literature review can feel overwhelming, especially if time and
                      resources are limited.
                    </span>
                    <span>
                      That's where a literature generator, such as the v0 Report, steps in to
                      streamline your process. Our literature review writing tool simplifies your
                      workflow by quickly finding and organizing important sources.
                    </span>
                    <span>
                      With our assistance, creating top-notch structured literature papers becomes a
                      matter of minutes, allowing you to dedicate more time to thorough analysis and
                      writing.
                    </span>
                  </p>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Topic Input</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Start the process by inputting your specific topic or research question.
                            This guides the direction of your literature content generation.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Research and Data Collection</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Our AI review generator is an easy-to-use tool that delves into
                            databases, journals, and digital resources to gather relevant data,
                            studies, and scholarly articles about your literature topic.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Structure and Template Selection</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Select from various literature templates tailored to different research
                            paper styles like analytical, expository, or argumentative. The AI then
                            organizes the gathered information according to your chosen literature
                            template.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Drafting the Content</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Using natural language processing (NLP) technologies, v0 Report
                            constructs a coherent and logically structured draft of your literature
                            review paper. It seamlessly integrates citations and formats them
                            correctly according to the designated academic style (APA, MLA, etc.).
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Review and Refinement</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Your literature review draft undergoes meticulous checks and revisions
                            to ensure accuracy, clarity, and coherence. By utilizing the v0 Report's
                            structured review with proper citations, you can review the draft, make
                            necessary adjustments, and refine your literature content before
                            finalizing the document.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Immediately Identify Sources for Any Literature Phrase
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      v0 Report is your go-to AI literature review tool seamlessly built into your
                      research paper editor. It's designed to make verifying online content a
                      breeze.
                    </span>
                    <span>
                      v0 Report comes with all sorts of literature research modes, from tracking
                      down support statistics to uncovering supporting arguments and more. It's like
                      having a whole research team at your fingertips!
                    </span>
                    <span>
                      With v0 Report on your side, writing and researching for your literature paper
                      feels like a walk in the park.
                    </span>
                  </p>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Effortlessly Inputs Literature References
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Our literature generator simplifies the citation process in MLA and APA
                      styles. It starts by extracting key details from each source, such as the
                      author, title, publication date, and URL, then formats them correctly
                      according to your selected citation style.
                    </span>
                    <span>
                      With built-in tools for generating citations and bibliographies accurately,
                      you can easily document your sources and avoid plagiarism.
                    </span>
                    <span>
                      Whether a student or a professional writer, creating a literature review saves
                      you time on citations, allowing you to focus on writing your paper with v0
                      Report.
                    </span>
                  </p>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Effortlessly Inputs Literature References
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      Struggling with writer's block, especially when it comes to literature papers?
                      Looking to create a standout paper but lacking inspiration?
                    </span>
                    <span>
                      Elevate your academic score and bid farewell to writer's block with the AI
                      literature report from v0 Report!
                    </span>
                    <span>
                      This literature review tool efficiently analyzes large data papers and uses
                      advanced algorithms to create original content.
                    </span>
                    <span>
                      Simply input your topic, and the Research Paper Generator from v0 Report will
                      swiftly deliver a well-crafted paper, saving you time and effort.
                    </span>
                  </p>
                </div>
              </div>
              <div className="hidden sm:block">
                <ToolLinks />
              </div>
            </div>
            <div className="w-fit grid gap-8 bg-sky-50 rounded-xl py-8 md:py-20 px-5 md:px-24 mx-2 md:mx-auto">
              <p className="text-2xl font-semibold text-gray-900 ">Get Started Today!</p>
              <p className="text-xl tracking-tight font-medium text-zinc-600 grid gap-2">
                <span>
                  Embrace the efficiency of the automated AI report writing tool with the v0 AI
                  Report Generator.
                </span>
                <span>
                  Sign up now or request a demo to see how our report writer tool can revolutionize
                  how you work with your report data.{' '}
                </span>
                <span>Transform your report analysis with v0 Report's accuracy and speed!</span>
              </p>
            </div>
            <div className="w-11/12 mx-auto my-24 md:w-fit text-center grid gap-8">
              <Link
                rel="canonical"
                href={isSubscribed ? '/home' : '/'}
                className="flex justify-center w-fit mx-auto"
              >
                <Image
                  className="object-scale-down h-8 w-8"
                  src="/images/logo.png"
                  alt="Logo"
                  width={200}
                  height={300}
                />
                <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                  v0 report
                </p>
              </Link>
              <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900 ">
                Experience the future of document generation today <br />
                with v0 Report!
              </h2>
              <Link
                className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                href={'/auth/signup'}
              >
                Get started
              </Link>
            </div>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
